import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Chip,
    Divider,
    CircularProgress,
    Box,
} from "@mui/material";

const formatDateTime = (timestamp) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleString("pt-BR");
};

const renderResumo = (items = []) => {
    if (!items.length) {
        return (
            <Typography variant="caption" color="text.secondary">
                Nenhum item registrado.
            </Typography>
        );
    }

    return (
        <Stack spacing={0.5}>
            {items.map((item, index) => (
                <Box key={`${item.imovelId || "sem-id"}-${index}`}>
                    <Typography variant="body2" fontWeight={600}>
                        {item?.codigo ? `#${item.codigo}` : item?.imovelId || "Sem ID"}
                        {item?.titulo ? ` — ${item.titulo}` : ""}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        {[item?.modalidade, item?.cidade, item?.bairro]
                            .filter(Boolean)
                            .join(" • ") || "Sem detalhes"}
                    </Typography>
                </Box>
            ))}
        </Stack>
    );
};

const TotalImoveisHistoryDialog = ({
    open,
    onClose,
    historico,
    loading,
    verde,
    bege,
    cinza,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
        >
            <DialogTitle sx={{ color: cinza, fontWeight: 700 }}>
                Histórico do Total de Imóveis
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Stack alignItems="center" justifyContent="center" py={6} spacing={2}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            Carregando histórico...
                        </Typography>
                    </Stack>
                ) : historico.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum histórico encontrado.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {historico.map((item) => {
                            const delta = Number(item?.delta || 0);
                            const deltaLabel =
                                delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : "0";

                            return (
                                <Box
                                    key={item.id}
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 2,
                                        p: 2,
                                        bgcolor: "#fff",
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        alignItems={{ xs: "flex-start", sm: "center" }}
                                        justifyContent="space-between"
                                        mb={1}
                                    >
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {formatDateTime(item?.timestamp)}
                                        </Typography>

                                        <Chip
                                            label={`Δ ${deltaLabel}`}
                                            size="small"
                                            sx={{
                                                bgcolor:
                                                    delta > 0 ? "#e8f5e9" : delta < 0 ? "#ffebee" : bege,
                                                color: cinza,
                                                fontWeight: 700,
                                            }}
                                        />
                                    </Stack>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
                                        <Chip label={`Antes: ${item?.beforeCount ?? 0}`} size="small" />
                                        <Chip label={`Depois: ${item?.afterCount ?? 0}`} size="small" />
                                        <Chip
                                            label={`Entraram: ${item?.addedIds?.length ?? 0}`}
                                            size="small"
                                            sx={{ bgcolor: "#e8f5e9" }}
                                        />
                                        <Chip
                                            label={`Saíram: ${item?.removedIds?.length ?? 0}`}
                                            size="small"
                                            sx={{ bgcolor: "#ffebee" }}
                                        />
                                    </Stack>

                                    {!!item?.addedSummaries?.length && (
                                        <Box mb={1.5}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                sx={{ color: verde, mb: 0.5 }}
                                            >
                                                Imóveis adicionados
                                            </Typography>
                                            {renderResumo(item.addedSummaries)}
                                        </Box>
                                    )}

                                    {!!item?.removedSummaries?.length && (
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight={700}
                                                sx={{ color: "#c62828", mb: 0.5 }}
                                            >
                                                Imóveis removidos
                                            </Typography>
                                            {renderResumo(item.removedSummaries)}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default TotalImoveisHistoryDialog;